import { FC, PropsWithChildren, ReactElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Combobox, ComboboxOption } from "@/components/_molecules/combobox/combobox";

import { useGetStatusListForWorkspace } from "../hooks/api/use-get-status-list-for-workspace";
import { Status } from "../models/status-model";

import { StatusSelectDropdownItem } from "./status-select-dropdown-item";

export interface StatusSelectDropdownProps extends PropsWithChildren {
  workspaceId: string;
  asChild?: boolean;
  selected?: {
    statusId: string;
  } | null;
  onSelect?: (status: { id: string } | null) => void;
  side?: "top" | "right" | "bottom" | "left";
  isReadOnly?: boolean;
}

export const StatusSelectDropdown: FC<StatusSelectDropdownProps> = ({
  workspaceId,
  children,
  side,
  asChild,
  onSelect,
  isReadOnly,
  ...props
}) => {
  const { t } = useTranslation("common");
  const [selectedStatus, setSelectedStatus] = useState(props.selected ? props.selected : null);
  const { statusList } = useGetStatusListForWorkspace({ workspaceId: workspaceId });

  const comboboxOptions = useMemo(() => {
    const statusOptions = statusList.flatMap((status) => {
      if (!status) {
        return [];
      }
      return {
        id: status.id,
        value: status.name,
        original: status,
      };
    });
    return [{ id: "-1", value: t("notAssigned"), original: null }, ...statusOptions];
  }, [statusList, t]);

  const renderItem = (option: ComboboxOption<Status>): ReactElement => {
    const status = option.original;

    const selected = (option.id === "-1" && selectedStatus === null) || selectedStatus?.statusId === option.id;
    return <StatusSelectDropdownItem status={status} selected={selected} />;
  };

  const handleUserSelected = (selectedOption: ComboboxOption<Status> | null) => {
    if (!selectedOption) {
      return;
    }

    if (selectedOption.id === "-1") {
      setSelectedStatus(null);
      if (onSelect) {
        onSelect(null);
      }
      return;
    }

    setSelectedStatus({ statusId: selectedOption.id });
    if (onSelect) {
      onSelect(selectedOption);
    }
  };

  useEffect(() => {
    setSelectedStatus(props.selected ?? null);
  }, [props.selected]);

  if (!comboboxOptions) {
    return <></>;
  }

  return (
    <Combobox
      asChild={asChild}
      options={comboboxOptions}
      renderItem={renderItem}
      onSelect={handleUserSelected}
      side={side}
      isReadOnly={isReadOnly}
    >
      {children}
    </Combobox>
  );
};
